import { useCallback, useEffect, useState } from "react";
import "./Wordle.css";

const WORD_LENGTH = 5;
const MAX_ATTEMPTS = 6;

export default function WordGame() {
	const [attempts, setAttempts] = useState<string[]>([]);
	const [currentGuess, setCurrentGuess] = useState<string>("");
	const [targetWord, setTargetWord] = useState<string>("");
	const [keyboardStatuses, setKeyboardStatuses] = useState<{
		[key: string]: string;
	}>({});
	const [gameOver, setGameOver] = useState<boolean>(false);
	const [gameResult, setGameResult] = useState<"win" | "lose" | "">("");

	useEffect(() => {
		async function fetchWord() {
			try {
				const response = await fetch(
					"https://api.datamuse.com/words?sp=?????" // Query for 5-letter words
				);
				const words = await response.json();
				if (words.length > 0) {
					const randomWord =
						words[Math.floor(Math.random() * words.length)].word;
					setTargetWord(randomWord.toUpperCase());
				}
			} catch (error) {
				// do nothing
			}
		}

		fetchWord();
	}, []);

	const updateKeyboardStatuses = useCallback(
		(guess: string) => {
			setKeyboardStatuses((statuses) => {
				const updatedStatuses = { ...statuses };
				guess.split("").forEach((letter, index) => {
					if (targetWord[index] === letter) {
						updatedStatuses[letter] = "correct";
					} else if (
						targetWord.includes(letter) &&
						updatedStatuses[letter] !== "correct"
					) {
						updatedStatuses[letter] = "present";
					} else if (!targetWord.includes(letter)) {
						updatedStatuses[letter] = "absent";
					}
				});
				return updatedStatuses;
			});
		},
		[targetWord]
	);

	const handleKeyPress = useCallback((key: string) => {
		if (gameOver) return;

		if (key === "Enter") {
			if (currentGuess.length === WORD_LENGTH) {
				updateKeyboardStatuses(currentGuess);
				const newAttempts = [...attempts, currentGuess];
				setAttempts(newAttempts);
				setCurrentGuess("");

				if (currentGuess === targetWord) {
					setGameOver(true);
					setGameResult("win");
				} else if (newAttempts.length === MAX_ATTEMPTS) {
					setGameOver(true);
					setGameResult("lose");
				}
			}
		} else if (key === "Backspace") {
			setCurrentGuess(currentGuess.slice(0, -1));
		} else if (currentGuess.length < WORD_LENGTH) {
			setCurrentGuess(currentGuess + key.toUpperCase());
		}
	}, [attempts, currentGuess, gameOver, targetWord, updateKeyboardStatuses]);

	useEffect(() => {
		const handlePhysicalKeyPress = (event: KeyboardEvent) => {
			if (event.altKey || event.ctrlKey || event.metaKey) return;
			if (event.key === "Enter" && event.target instanceof HTMLButtonElement) {
				return;
			}
			if (event.key === "Enter" || event.key === "Backspace") {
				event.preventDefault();
				handleKeyPress(event.key);
			} else if (/^[a-z]$/i.test(event.key)) {
				handleKeyPress(event.key);
			}
		};

		window.addEventListener("keydown", handlePhysicalKeyPress);
		return () => window.removeEventListener("keydown", handlePhysicalKeyPress);
	}, [handleKeyPress]);

	const restartGame = () => {
		setAttempts([]);
		setCurrentGuess("");
		setKeyboardStatuses({});
		setGameOver(false);
		setGameResult("");
	};

	const getKeyboardLetterStatus = (letter: string) => {
		return keyboardStatuses[letter] || "";
	};

	const getLetterStatus = (letter: string, index: number) => {
		if (targetWord[index] === letter) {
			return "correct";
		} else if (targetWord.includes(letter)) {
			return "present";
		} else {
			return "absent";
		}
	};

	return (
		<div className="game-container">
			<div className="word-grid" role="grid" aria-label="Wordle board">
				{attempts.map((attempt, i) => (
					<div key={i} className="word-row" role="row">
						{attempt.split("").map((letter, j) => (
							<span
								key={j}
								role="gridcell"
								aria-label={`${letter}, ${getLetterStatus(
									letter,
									j
								)}`}
								className={`letter ${getLetterStatus(
									letter,
									j
								)}`}>
								{letter}
							</span>
						))}
					</div>
				))}
				{!gameOver && attempts.length < MAX_ATTEMPTS && (
					<div className="word-row" role="row">
						{Array.from(Array(WORD_LENGTH)).map((_, i) => (
							<span key={i} className="letter" role="gridcell">
								{currentGuess[i] ?? ""}
							</span>
						))}
					</div>
				)}
			</div>
			<p className="feedback-legend">
				✓ Correct · • In word, wrong spot · × Not in word
			</p>
			{gameOver ? (
				<div className="game-over-message" role="status" aria-live="polite">
					{gameResult === "win" ? (
						<p>Well done! You have guessed the word correctly!</p>
					) : (
						<p>Unlucky! The word was {targetWord}.</p>
					)}
					<button
						type="button"
						className="restart-button"
						onClick={restartGame}>
						Play again
					</button>
				</div>
			) : (
				<div className="keyboard">
					<div className="keyboard-row">
						{Array.from("QWERTYUIOP").map((letter) => (
							<button
								type="button"
								key={letter}
								onClick={() => handleKeyPress(letter)}
								aria-label={`${letter}${getKeyboardLetterStatus(letter) ? `, ${getKeyboardLetterStatus(letter)}` : ""}`}
								className={`keyboard-button ${getKeyboardLetterStatus(
									letter
								)}`}>
								{letter}
							</button>
						))}
					</div>
					<div className="keyboard-row">
						{Array.from("ASDFGHJKL").map((letter) => (
							<button
								type="button"
								key={letter}
								onClick={() => handleKeyPress(letter)}
								aria-label={`${letter}${getKeyboardLetterStatus(letter) ? `, ${getKeyboardLetterStatus(letter)}` : ""}`}
								className={`keyboard-button ${getKeyboardLetterStatus(
									letter
								)}`}>
								{letter}
							</button>
						))}
					</div>
					<div className="keyboard-row">
						<button
							type="button"
							onClick={() => handleKeyPress("Enter")}
							aria-label="Enter guess"
							className="keyboard-button special-button">
							Enter
						</button>
						{Array.from("ZXCVBNM").map((letter) => (
							<button
								type="button"
								key={letter}
								onClick={() => handleKeyPress(letter)}
								aria-label={`${letter}${getKeyboardLetterStatus(letter) ? `, ${getKeyboardLetterStatus(letter)}` : ""}`}
								className={`keyboard-button ${getKeyboardLetterStatus(
									letter
								)}`}>
								{letter}
							</button>
						))}
						<button
							type="button"
							onClick={() => handleKeyPress("Backspace")}
							aria-label="Delete last letter"
							className="keyboard-button special-button">
							Delete
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
